import { useEffect, useState } from "react";
import { useDropzone } from "react-dropzone";
import { useFormContext, useWatch } from "react-hook-form";
import { t } from "@/i18n-js/instance";
import { Icon } from "@circle-react/components/shared/Icon";
import { TippyV2 } from "@circle-react/components/shared/TippyV2";
import { classNames } from "@circle-react/helpers/twMergeWithCN";
import { useDebouncedValue } from "@circle-react/hooks/utils/useDebouncedValue";
import { Form } from "@circle-react-uikit/Form";
import { Typography } from "@circle-react-uikit/Typography";
import {
  ATTACHMENT_STATUS_COMPLETED,
  ATTACHMENT_STATUS_UPLOADING,
  TICKET_PRIORITIES,
  TICKET_TYPES,
} from "../../constants";
import { FormAttachment } from "./FormAttachment";
import { FormItem } from "./FormItem";
import "../../assets/styles/send-us-a-message-form.css";

interface SendUsAMessageFormProps {
  attachments: { url: string; filename: string; completed: boolean }[];
  cancelAttachment: (url: string) => void;
  isLoading: boolean;
  onFormChange: (data: {
    text: string;
    type: string;
    priority: string;
  }) => void;
  uploadAttachments: (files: File[]) => void;
}

type TicketType = (typeof TICKET_TYPES)[number];
type TicketPriority = (typeof TICKET_PRIORITIES)[number];

const TEN_SECONDS = 10 * 1000; // 10 seconds in milliseconds

export const SendUsAMessageForm = ({
  isLoading,
  uploadAttachments,
  attachments,
  cancelAttachment,
  onFormChange,
}: SendUsAMessageFormProps) => {
  const { formState } = useFormContext();
  const [text, type, priority] = useWatch<
    {
      text: string;
      type: string;
      priority: string;
    },
    ["text", "type", "priority"]
  >({
    name: ["text", "type", "priority"],
  });
  const hasTypeError = Boolean(formState.errors.type);
  const hasPriorityError = Boolean(formState.errors.priority);
  const isFormDirty = text && type && priority;
  const debouncedText = useDebouncedValue(text, TEN_SECONDS);
  const [ticketTypes, setTicketTypes] = useState<TicketType[]>([
    ...TICKET_TYPES,
  ]);
  const [ticketPriorities, setTicketPriorities] = useState<TicketPriority[]>([
    ...TICKET_PRIORITIES,
  ]);

  const areAllAttachmentsComplete = attachments.every(
    attachment => attachment.completed,
  );

  const shouldShowValidationError =
    formState.isSubmitted && (!isFormDirty || !areAllAttachmentsComplete);

  const { getRootProps, getInputProps, inputRef } = useDropzone({
    onDrop: uploadAttachments,
    noClick: true,
  });

  const openFilePicker = (event: React.MouseEvent) => {
    event.preventDefault();
    inputRef.current?.click();
  };

  const handleTypeSearch = (query: string) => {
    if (query === "") {
      setTicketTypes([...TICKET_TYPES]);
    } else {
      const filteredTicketTypes = TICKET_TYPES.filter(ticketType =>
        ticketType.value.toLowerCase().includes(query.toLowerCase()),
      );
      setTicketTypes(filteredTicketTypes);
    }
  };

  const handlePrioritySearch = (query: string) => {
    if (query === "") {
      setTicketPriorities([...TICKET_PRIORITIES]);
    } else {
      setTicketPriorities(
        TICKET_PRIORITIES.filter(priority =>
          priority.text.toLowerCase().includes(query.toLowerCase()),
        ),
      );
    }
  };

  useEffect(() => {
    if (debouncedText || type || priority) {
      onFormChange({ text, type, priority });
    }
  }, [debouncedText, type, priority]);

  useEffect(() => {
    const sendMessageFormContainer = document.getElementById(
      "send-message-form-container",
    );
    sendMessageFormContainer?.scrollIntoView();
  }, []);

  return (
    <div className="flex max-h-full flex-col px-6">
      <FormItem name="type" hideError>
        <Form.Select
          name="type"
          data={ticketTypes}
          onSearch={handleTypeSearch}
          inputContainerClassName={hasTypeError ? "form-error" : ""}
        />
      </FormItem>
      <FormItem name="priority" className="!pt-2" hideError>
        <Form.Select
          name="priority"
          inputContainerClassName={classNames(
            "priority-select-input-container",
            hasPriorityError ? "form-error" : "",
          )}
          data={ticketPriorities}
          onSearch={handlePrioritySearch}
        />
      </FormItem>
      <FormItem className="!pt-2" name="text" hideError>
        <Form.Input name="text" variant="textarea" rows={12} />
      </FormItem>
      <div>
        <Typography.BodyXs color="text-tertiary" weight="regular">
          {t("help_widget.report_bug.what_did_you_find")}
        </Typography.BodyXs>
      </div>
      {attachments.length > 0 && (
        <div className="mt-6 flex flex-col gap-2">
          {attachments.map(attachment => (
            <FormAttachment
              key={attachment.url}
              attachment={{
                ...attachment,
                status: attachment.completed
                  ? ATTACHMENT_STATUS_COMPLETED
                  : ATTACHMENT_STATUS_UPLOADING,
              }}
              onCancel={cancelAttachment}
            />
          ))}
        </div>
      )}
      <div className="my-6 flex justify-between" {...getRootProps()}>
        <input {...getInputProps()} />
        <TippyV2
          placement="right"
          content={t("tiptap.attach_file")}
          className="text-dark visited:text-dark hover:bg-tertiary hover:text-dark focus:bg-tertiary flex w-10 items-center justify-center rounded-lg text-center"
        >
          <button
            type="button"
            aria-label={t("tiptap.attach_file")}
            onClick={openFilePicker}
            className="p-2"
          >
            <Icon type="hw-paperclip" size={20} />
          </button>
        </TippyV2>

        <div className="flex flex-row items-center text-right">
          {shouldShowValidationError && (
            <div className="max-w-[200px] px-4 text-right">
              <Typography.BodyXs
                color="text-v2-danger"
                weight="regular"
                leading="leading-[18px]"
              >
                {t("help_widget.report_bug.validation_error")}
              </Typography.BodyXs>
            </div>
          )}
          <button
            className="bg-dark-primary-button rounded-3xl px-6 py-2.5 font-bold leading-5 text-white hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-50"
            disabled={
              isLoading ||
              shouldShowValidationError ||
              !areAllAttachmentsComplete
            }
            type="submit"
          >
            <div className="flex items-center justify-center gap-2">
              {isLoading && <Icon type="loader" size={16} />}
              {t("support_widget.send_message_form.button_label")}
            </div>
          </button>
        </div>
      </div>
    </div>
  );
};
